import { useEffect, useState } from "react";
import { Icons } from "../components/Icons.js";
import { api, ApiError, type StandaloneNoteDetail } from "../lib/api.js";
import { useAuth } from "../auth/AuthContext.js";
import { summaryDone, summaryFailed, summaryStarted } from "../components/SummaryToast.js";
import type { EditTarget } from "./types.js";

/**
 * Fenêtre d'édition (cahier des charges §9). Barre d'actions sur la transcription / le résumé :
 * éditer (zone de texte), enregistrer, copier, envoyer par e-mail ; pour les notes,
 * **relancer** la transcription (grisé si l'audio est purgé) et générer un résumé.
 */
export function EditWindow({ target, onClose, onChanged }: { target: EditTarget; onClose: () => void; onChanged?: () => void }) {
  const [content, setContent] = useState("");
  const [detail, setDetail] = useState<StandaloneNoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ msg: string; err: boolean } | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  // Destinataire proposé par défaut : soi-même. C'est l'envoi le plus courant (s'expédier une note
  // pour la retrouver dans sa boîte), et l'adresse reste modifiable.
  const { user } = useAuth();
  const [emailAddr, setEmailAddr] = useState(user?.email ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      if (target.kind === "day") {
        const d = await api.dayNoteContent(target.id);
        if (!cancelled) setContent(d.content);
      } else if (target.kind === "standalone") {
        const d = await api.standaloneNote(target.id);
        if (!cancelled) {
          setDetail(d);
          setContent(d.transcript);
        }
      } else {
        const d = await api.summaryContent(target.id);
        if (!cancelled) setContent(d.content);
      }
    };
    load()
      .catch((e) => !cancelled && setFlash({ msg: e instanceof ApiError ? e.message : "Chargement impossible.", err: true }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [target]);

  const flashOk = (msg: string) => setFlash({ msg, err: false });
  const flashErr = (e: unknown) => setFlash({ msg: e instanceof ApiError ? e.message : "Action impossible.", err: true });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setFlash(null);
    try {
      await fn();
    } catch (e) {
      flashErr(e);
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      if (target.kind === "day") await api.saveDayNote(target.id, content);
      else if (target.kind === "standalone") await api.saveStandaloneTranscript(target.id, content);
      else await api.saveSummary(target.id, content);
      flashOk("Enregistré.");
      onChanged?.();
    });

  const copy = () =>
    run(async () => {
      await navigator.clipboard?.writeText(content);
      flashOk("Copié dans le presse-papier.");
    });

  // « Copier brut » : retire les titres horodatés « ## HH:MM » et les lignes vides (utile quand
  // l'horodatage est désactivé et que les entrées sont séparées par des lignes vides). L'en-tête
  // « # Note du jour — … » est conservé. Proposé aussi sur un résumé, où il compacte le texte en
  // supprimant les lignes vides entre les paragraphes.
  const copyRaw = () =>
    run(async () => {
      const raw = content
        .split("\n")
        .filter((line) => !/^\s*##\s*\d{1,2}:\d{2}\s*$/.test(line) && line.trim().length > 0)
        .join("\n");
      await navigator.clipboard?.writeText(raw);
      flashOk("Copié (sans horodatage).");
    });

  // Note du jour : suppression DÉFINITIVE (pas de corbeille) → confirmation en deux temps.
  // Notes indépendantes et résumés : corbeille (récupérable), suppression directe.
  const remove = () => {
    if (target.kind === "day" && !confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    void run(async () => {
      if (target.kind === "day") {
        await api.deleteDayNote(target.id);
        flashOk("Journée supprimée définitivement.");
      } else if (target.kind === "standalone") {
        await api.deleteStandaloneNote(target.id);
        flashOk("Envoyé à la corbeille.");
      } else {
        await api.deleteSummary(target.id);
        flashOk("Envoyé à la corbeille.");
      }
      onChanged?.();
      onClose();
    });
  };

  const sendEmail = () =>
    run(async () => {
      if (!emailAddr.trim()) {
        setFlash({ msg: "Saisissez une adresse e-mail.", err: true });
        return;
      }
      await api.sendEmail(emailAddr.trim(), target.label, content);
      flashOk("Envoyé par e-mail.");
      setEmailOpen(false);
    });

  const retranscribe = () =>
    run(async () => {
      const res = await api.retranscribeStandalone(target.id);
      setContent(res.transcript);
      flashOk("Transcription relancée.");
      onChanged?.();
    });

  // La génération passe par le modèle distant (plusieurs secondes) : l'attente et le résultat sont
  // signalés par le bandeau global, qui survit à la fermeture de cette fenêtre et permet d'ouvrir le
  // résumé produit d'un appui.
  const genSummary = () =>
    run(async () => {
      const label = `Résumé de « ${target.label} »`;
      summaryStarted(label);
      try {
        const res = await api.generateStandaloneSummary(target.id);
        summaryDone(label, res.summaryId);
        onChanged?.();
      } catch (e) {
        summaryFailed(e instanceof ApiError ? e.message : "Génération du résumé impossible.");
        throw e;
      }
    });

  const isStandalone = target.kind === "standalone";
  const canRetranscribe = isStandalone && detail?.hasAudio === true;

  return (
    <div className="editwin-scrim" onClick={onClose}>
      <div className="editwin" onClick={(e) => e.stopPropagation()}>
        <header className="editwin-head">
          <div className="editwin-title">{target.label}</div>
          <button className="iconbtn" onClick={onClose} aria-label="Fermer">
            <Icons.Close size={20} />
          </button>
        </header>

        <div className="editwin-actions">
          <button className="ewbtn primary" onClick={save} disabled={busy || loading}>
            <Icons.Check size={16} /> Enregistrer
          </button>
          <button className="ewbtn" onClick={copy} disabled={busy || loading} title="Copier" aria-label="Copier">
            <Icons.Copy size={16} /> <span className="ewbtn-t">Copier</span>
          </button>
          {(target.kind === "day" || target.kind === "summary") && (
            <button
              className="ewbtn"
              onClick={copyRaw}
              disabled={busy || loading}
              aria-label="Copier brut"
              title="Copier le texte sans les horodatages ni lignes vides"
            >
              <Icons.CopyPlain size={16} /> <span className="ewbtn-t">Copier brut</span>
            </button>
          )}
          <button className="ewbtn" onClick={() => setEmailOpen((o) => !o)} disabled={busy || loading} title="Envoyer par e-mail" aria-label="Envoyer par e-mail">
            <Icons.Mail size={16} /> <span className="ewbtn-t">E-mail</span>
          </button>
          {isStandalone && (
            <button className="ewbtn" onClick={retranscribe} disabled={busy || loading || !canRetranscribe} aria-label="Retranscrire" title={canRetranscribe ? "Re-transcrire l'audio" : "Audio purgé : re-transcription indisponible"}>
              <Icons.Refresh size={16} /> <span className="ewbtn-t">Retranscrire</span>
            </button>
          )}
          {isStandalone && (
            <button className="ewbtn" onClick={genSummary} disabled={busy || loading} title="Générer un résumé" aria-label="Générer un résumé">
              <Icons.Doc size={16} /> <span className="ewbtn-t">Résumé</span>
            </button>
          )}
          {confirmDelete ? (
            <>
              <button className="ewbtn danger" onClick={remove} disabled={busy || loading} title="Confirmer la suppression définitive">
                <Icons.Trash size={16} /> Confirmer
              </button>
              <button className="ewbtn" onClick={() => setConfirmDelete(false)} disabled={busy || loading}>
                Annuler
              </button>
            </>
          ) : (
            <button
              className="ewbtn danger"
              onClick={remove}
              disabled={busy || loading}
              aria-label="Supprimer"
              title={target.kind === "day" ? "Supprimer définitivement la journée" : "Envoyer à la corbeille"}
            >
              <Icons.Trash size={16} /> <span className="ewbtn-t">Supprimer</span>
            </button>
          )}
        </div>
        {confirmDelete && (
          <div className="editwin-confirm">
            Êtes-vous sûr de vouloir supprimer la journée ? C'est définitif.
          </div>
        )}

        <div className="editwin-body">
          {emailOpen && (
            <div className="editwin-emailrow">
              <input type="email" placeholder="adresse@exemple.com" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} />
              <button className="ewbtn primary" onClick={sendEmail} disabled={busy}>
                Envoyer
              </button>
            </div>
          )}
          {loading ? (
            <div className="listspinner"><div className="spinner" /></div>
          ) : (
            <textarea className="editwin-ta" value={content} onChange={(e) => setContent(e.target.value)} />
          )}
          {isStandalone && detail && !detail.hasAudio && (
            <div className="editwin-note">L'audio a été purgé : la re-transcription n'est plus possible.</div>
          )}
          {isStandalone && detail && detail.summaries.length > 0 && (
            <div className="editwin-note">{detail.summaries.length} résumé(s) — voir l'écran Comptes rendus.</div>
          )}
          {flash && <div className={"editwin-flash" + (flash.err ? " err" : "")}>{flash.msg}</div>}
        </div>
      </div>
    </div>
  );
}
